import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import SalviaLayout from "@/components/SalviaLayout";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ClipboardList,
  Eye,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  Users,
} from "lucide-react";

type Campaign = {
  id: number;
  companyId: number;
  companyName: string | null;
  companyCnpj?: string | null;
  status: string;
  templateId: number | null;
  templateVersion: string | null;
  kMin: number | null;
  consolidationMode: string | null;
  totalResponses: number;
  hasSnapshot: boolean;
  createdAt: string | null;
  activatedAt: string | null;
  closedAt: string | null;
  endsAt: string | null;
};

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: React.ReactNode }> = {
    ACTIVE: { label: "Ativa", variant: "default", icon: <CheckCircle2 className="w-3 h-3" /> },
    CLOSED: { label: "Encerrada", variant: "secondary", icon: <XCircle className="w-3 h-3" /> },
    DRAFT: { label: "Rascunho", variant: "outline", icon: <Clock className="w-3 h-3" /> },
    CANCELLED: { label: "Cancelada", variant: "destructive", icon: <XCircle className="w-3 h-3" /> },
  };
  const cfg = map[status] ?? { label: status, variant: "outline" as const, icon: null };
  return (
    <Badge variant={cfg.variant} className="flex items-center gap-1 text-xs">
      {cfg.icon}
      {cfg.label}
    </Badge>
  );
}

function formatDate(val: string | null | undefined) {
  if (!val) return "—";
  return new Date(val).toLocaleDateString("pt-BR");
}

function ProgressBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  const color = pct >= 100 ? "bg-green-500" : pct >= 50 ? "bg-yellow-500" : "bg-red-400";
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
        <div className={`h-2 rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-muted-foreground whitespace-nowrap">{value}/{max}</span>
    </div>
  );
}

export default function QuestionariosRH() {
  usePageTitle("Questionários");
  const [, setLocation] = useLocation();
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("all");

  const listQuery = trpc.questionnaire.history.listMyCampaigns.useQuery(undefined, {
    refetchOnWindowFocus: false,
  });

  const campaigns: Campaign[] = listQuery.data ?? [];
  const companyOptions = useMemo(
    () =>
      campaigns
        .map((campaign) => ({
          id: String(campaign.companyId),
          name: campaign.companyName || `Empresa ${campaign.companyId}`,
        }))
        .filter((company, index, array) => array.findIndex((item) => item.id === company.id) === index)
        .sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    [campaigns],
  );
  const filteredCampaigns = useMemo(
    () => campaigns.filter((campaign) => selectedCompanyId === "all" || String(campaign.companyId) === selectedCompanyId),
    [campaigns, selectedCompanyId],
  );

  return (
    <SalviaLayout>
      <div className="p-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <ClipboardList className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Questionários</h1>
            <p className="text-sm text-muted-foreground">
              Campanhas de aplicação de questionários das empresas do seu escopo
            </p>
          </div>
        </div>

        {/* Loading */}
        {listQuery.isLoading && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Carregando campanhas...
          </div>
        )}

        {/* Error */}
        {listQuery.isError && (
          <div className="flex items-center gap-2 text-destructive py-8">
            <AlertCircle className="w-5 h-5" />
            <span>Ocorreu um erro ao carregar as campanhas. Tente novamente.</span>
          </div>
        )}

        {/* Empty */}
        {!listQuery.isLoading && !listQuery.isError && campaigns.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">Nenhuma campanha encontrada</p>
            <p className="text-sm mt-1">
              Ainda não há campanhas de questionário para as empresas do seu escopo.
            </p>
          </div>
        )}

        {!listQuery.isLoading && !listQuery.isError && campaigns.length > 0 && (
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-medium text-slate-700">Filtro por cliente</p>
              <p className="text-xs text-muted-foreground">
                Selecione uma empresa do seu escopo hierárquico para refinar a listagem de campanhas.
              </p>
            </div>
            <div className="w-full sm:w-80">
              <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas as empresas do escopo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as empresas do escopo</SelectItem>
                  {companyOptions.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Table */}
        {filteredCampaigns.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">ID</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Empresa</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Progresso</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Criada em</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Encerramento</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredCampaigns.map((campaign, idx) => (
                  <tr
                    key={campaign.id}
                    className={idx % 2 === 0 ? "bg-background" : "bg-muted/20"}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      #{campaign.id}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{campaign.companyName || "—"}</div>
                      <div className="text-xs text-muted-foreground">{campaign.companyCnpj || `ID ${campaign.companyId}`}</div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={campaign.status} />
                    </td>
                    <td className="px-4 py-3 min-w-[160px]">
                      {campaign.kMin ? (
                        <ProgressBar value={campaign.totalResponses} max={campaign.kMin} />
                      ) : (
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Users className="w-3 h-3" />
                          {campaign.totalResponses} resp.
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {formatDate(campaign.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {formatDate(campaign.endsAt ?? campaign.closedAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setLocation(`/questionarios/${campaign.id}`)}
                        className="flex items-center gap-1"
                      >
                        <Eye className="w-3 h-3" />
                        Detalhes
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Info note */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-md text-xs text-blue-700">
            Você está visualizando as campanhas das empresas do seu escopo em modo somente leitura. Use o filtro por cliente para focar em uma empresa específica quando necessário.

        </div>
      </div>
    </SalviaLayout>
  );
}
